/**
 * CacheStatsPopover - Popover con statistiche cache offline
 * Mostra conteggi record in cache e stato operazioni
 */
import React from 'react';
import { Database, Package, Truck, Clock, RefreshCw, Check } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import { Button } from '@/shared/ui/button';
import { useCacheStats } from '@/hooks/mobile/useCacheStats';
import { useMutationQueue } from '@/contexts/MutationQueueContext';
import { cn } from '@/lib/utils';

interface CacheStatsPopoverProps {
  children: React.ReactNode;
  className?: string;
}

export const CacheStatsPopover: React.FC<CacheStatsPopoverProps> = ({
  children,
  className,
}) => {
  const stats = useCacheStats();
  const { pendingCount, lastSyncResult, triggerPreload, isPreloading, lastSyncTime } = useMutationQueue();

  const formatTime = (date: Date | null) => {
    if (!date) return 'Mai';
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalCached = stats.raProfiles + stats.shopProducts + stats.shopDeliveries + stats.laundryMovements;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className={cn('w-72 p-0', className)}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b bg-muted/50">
          <h4 className="font-medium text-sm">Cache Offline</h4>
          <p className="text-xs text-muted-foreground">
            Ultimo preload: {formatTime(stats.lastPreloadTime)}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="p-4 space-y-3">
          {/* Dati in cache */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Dati in cache ({totalCached})
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-green-600" />
                <span>{stats.raProfiles} RA</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-orange-600" />
                <span>{stats.shopProducts} prodotti</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-blue-600" />
                <span>{stats.shopDeliveries} consegne</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fas fa-tshirt h-4 w-4 text-cyan-600 text-sm" />
                <span>{stats.laundryMovements} movimenti</span>
              </div>
            </div>
          </div>

          {/* Operazioni */}
          <div className="space-y-2 pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Operazioni
            </p>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span>{pendingCount} in coda</span>
              </div>
              {lastSyncResult && lastSyncResult.success > 0 && (
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <Check className="h-3 w-3" />
                  <span>{lastSyncResult.success} sincronizzate</span>
                </div>
              )}
            </div>
            {lastSyncTime && (
              <p className="text-xs text-muted-foreground">
                Ultimo sync: {formatTime(lastSyncTime)}
              </p>
            )}
          </div>
        </div>

        {/* Footer con azione */}
        <div className="px-4 py-3 border-t bg-muted/30">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => triggerPreload()}
            disabled={isPreloading}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', isPreloading && 'animate-spin')} />
            {isPreloading ? 'Sincronizzando...' : 'Aggiorna cache'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CacheStatsPopover;
